/*
 * PROD-7828 W4 — Bank payouts: pure response shaping, labels, the generate choices, form validation and
 * error routing, pinned to teacher_payouts.json by adapters.test.js. Accounts are only ever the server's
 * masked value or last 4 digits; nothing here sees a full number.
 */
import { EM_DASH, api_error, fmt } from '../format';
import { reason_length } from '../needs_attention/params';
import {
  ACTION_FAILED,
  ACTION_IGNORE,
  ACTION_SENT,
  KIND_NEW_ROUND,
  KIND_R1,
  KIND_REGENERATE
} from './requests';

const str = (v) => (typeof v === 'string' ? v : '');
const arr = (v) => (Array.isArray(v) ? v : []);
const shown = (v) => str(v) || EM_DASH;
const money = (v) => (typeof v === 'number' ? fmt(v, 2) : EM_DASH);
const cursor = (v) => (typeof v === 'number' && v > 0 ? v : null);

export const REASON_MIN_LEN = 5;
export const REASON_MAX_LEN = 1024;
export const BANK_REFERENCE_MAX = 64;
export const HOLIDAY_NAME_MAX = 128;
export const RESPONSE_MAX_BYTES = 2097152;

export const FORBIDDEN_TEXT = 'Only the teacher payments team can manage bank payouts.';
export const PARTIAL_NEEDS_INVOICE_TEXT =
  'No confirmed invoice for this month, so paid outside must be the full outstanding amount.';
export const BEFORE_CUTOVER_TEXT =
  'Months before the cutover month were paid outside this system, so they have no bank file.';

// ---- labels ----

const label_of = (map, code) => map[code] || { label: str(code) || EM_DASH, tone: 'gray' };

const ITEM_STATES = {
  IN_FILE: { label: 'In a bank file', tone: 'blue' },
  SENT: { label: 'Sent to bank', tone: 'green' },
  FAILED: { label: 'Did not go through', tone: 'red' },
  HELD: { label: 'Held for Ops', tone: 'orange' },
  CANCELLED: { label: 'Cancelled', tone: 'gray' },
  PAID_OUTSIDE: { label: 'Paid outside the file', tone: 'green' }
};
export const item_state_view = (code) => label_of(ITEM_STATES, code);

const MATCHES = {
  MATCHED_REFERENCE: { label: 'Matched by our reference', tone: 'green' },
  MATCHED_UTR: { label: 'Matched by bank reference', tone: 'green' },
  MATCHED_FALLBACK: { label: 'Matched by account and amount', tone: 'blue' },
  UNMATCHED: { label: 'No matching payment', tone: 'orange' },
  AMBIGUOUS: { label: 'Matches more than one payment', tone: 'orange' },
  NOT_OURS: { label: 'Not a teacher payment', tone: 'gray' },
  INVALID_LINE: { label: 'Line could not be read', tone: 'red' }
};
export const match_view = (code) => label_of(MATCHES, code);

const PROPOSED = {
  MARK_SENT: { label: 'Mark sent', tone: 'green' },
  MARK_FAILED: { label: 'Mark failed', tone: 'red' },
  NO_CHANGE: { label: 'No change (already recorded)', tone: 'gray' },
  IGNORE: { label: 'Ignore', tone: 'gray' },
  HOLD_UNKNOWN_STATUS: { label: 'Hold: bank status is not E or R', tone: 'orange' },
  HOLD_NO_UTR: { label: 'Hold: sent without a bank reference', tone: 'orange' },
  HOLD_AMOUNT_MISMATCH: { label: 'Hold: amount differs from our line', tone: 'orange' },
  HOLD_ACCOUNT_DIFFERS: { label: 'Hold: account differs from our line', tone: 'orange' },
  HOLD_CANCELLED_FILE: {
    label: 'Hold: line of a cancelled file (possible double payment)',
    tone: 'red'
  },
  HOLD_CONFLICT: { label: 'Hold: conflicts with an earlier bank answer', tone: 'orange' },
  HOLD_ITEM_HELD: { label: 'Hold: the payment is already held', tone: 'orange' },
  HOLD_PAID_OUTSIDE: { label: 'Hold: already paid outside the file', tone: 'red' },
  HOLD_DUPLICATE: { label: 'Hold: second line for the same payment', tone: 'orange' },
  HOLD_UNMATCHED: { label: 'Hold: no matching payment', tone: 'orange' },
  HOLD_AMBIGUOUS: { label: 'Hold: matches more than one payment', tone: 'orange' },
  HOLD_INVALID_LINE: { label: 'Hold: line could not be read', tone: 'red' },
  HOLD_BAD_UTR: {
    label: 'Hold: bank reference not valid — type the correct UTR to count as sent',
    tone: 'orange'
  }
};
export const proposed_view = (code) => label_of(PROPOSED, code);

const APPLIED = {
  PENDING: { label: 'Not applied yet', tone: 'purple' },
  APPLIED: { label: 'Applied', tone: 'green' },
  HELD: { label: 'Held for Ops', tone: 'orange' },
  IGNORED: { label: 'Ignored', tone: 'gray' },
  NO_CHANGE: { label: 'No change', tone: 'gray' },
  RESOLVED: { label: 'Resolved by Ops', tone: 'blue' }
};
export const applied_view = (code) => label_of(APPLIED, code);

export const OPS_ACTIONS = [
  { value: ACTION_SENT, label: 'Count as sent' },
  { value: ACTION_FAILED, label: 'Count as failed' },
  { value: ACTION_IGNORE, label: 'Ignore' }
];
const ops_action_label = (code) => {
  const hit = OPS_ACTIONS.find((a) => a.value === code);
  return hit ? hit.label : str(code);
};

const TEACHER_STATES = {
  BEFORE_CUTOVER: { label: 'Paid outside this system', tone: 'gray' },
  NOT_CLOSED: { label: 'Month not closed', tone: 'gray' },
  NOTHING_TO_PAY: { label: 'Nothing to pay', tone: 'gray' },
  PAYOUT_ON_HOLD_INVOICE: { label: 'On hold: invoice', tone: 'orange' },
  PAYOUT_ON_HOLD_DETAILS: { label: 'On hold: payment details', tone: 'orange' },
  CONFIRMED_NEXT_PAYMENT: { label: 'In the next payment', tone: 'purple' },
  PROCESSING: { label: 'Processing', tone: 'blue' },
  SENT: { label: 'Sent to bank', tone: 'green' },
  FAILED: { label: 'Did not go through', tone: 'red' },
  PAID_SEPARATELY: { label: 'Paid separately', tone: 'green' }
};
export const teacher_state_view = (code) => label_of(TEACHER_STATES, code);

const FILE_STATUSES = {
  ACTIVE: { label: 'Active', tone: 'green' },
  CANCELLED: { label: 'Cancelled', tone: 'gray' }
};
export const file_status_view = (code) => label_of(FILE_STATUSES, code);

export const CLAIM_STATUSES = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ALL', label: 'All' }
];
const CLAIM_TONES = {
  OPEN: { label: 'Open', tone: 'orange' },
  RESOLVED: { label: 'Resolved', tone: 'green' }
};
export const claim_status_view = (code) => label_of(CLAIM_TONES, code);

// File column A: I = HDFC to HDFC, N = NEFT to another bank.
const MODES = { I: 'HDFC transfer', N: 'NEFT' };

const teacher_name = (v) => str(v) || 'Name not available';

// ---- eligibility and files ----

const file_view = (f) => {
  const d = f || {};
  const counts = d.line_counts && typeof d.line_counts === 'object' ? d.line_counts : {};
  return {
    uuid: str(d.uuid),
    round_no: typeof d.round_no === 'number' ? d.round_no : 0,
    kind: str(d.kind),
    file_name: str(d.file_name),
    status: str(d.status),
    status_view: file_status_view(d.status),
    payment_date: shown(d.payment_date),
    row_count: typeof d.row_count === 'number' ? d.row_count : 0,
    total: money(d.total),
    generated_by: shown(d.generated_by_name),
    generated_at: shown(d.generated_at_ist),
    replaces_file_uuid: str(d.replaces_file_uuid),
    cancelled_at: str(d.cancelled_at_ist),
    cancel_reason: str(d.cancel_reason),
    line_counts: counts,
    counts_text: Object.keys(counts)
      .sort()
      .map((k) => `${counts[k]} ${item_state_view(k).label.toLowerCase()}`)
      .join(', '),
    downloads: arr(d.downloads).map((x) => ({ by: shown(x && x.by), at: shown(x && x.at_ist) })),
    // The bank has answered some line: regenerating would cancel lines it may also pay.
    answered: Object.keys(counts).some((k) => k !== 'IN_FILE' && k !== 'CANCELLED' && counts[k] > 0)
  };
};

const left_out_view = (r) => ({
  teacher_uuid: str(r && r.teacher_uuid),
  teacher_name: teacher_name(r && r.teacher_name),
  reason: str(r && r.reason),
  reason_text: str(r && r.reason_text) || str(r && r.reason) || EM_DASH,
  outstanding: r && typeof r.outstanding === 'number' ? money(r.outstanding) : EM_DASH
});

// Warning-only row flags (S15); they never change selection or the generate request.
const FLAG_LARGE = 'LARGE_AMOUNT';
const FLAG_ABOVE_AVERAGE = 'ABOVE_AVERAGE';
const FLAG_TONES = { [FLAG_LARGE]: 'orange', [FLAG_ABOVE_AVERAGE]: 'yellow' };

// three_month_average is a rupees value (number or decimal string) or null.
const average = (v) => {
  const shown_avg = typeof v === 'number' || typeof v === 'string' ? fmt(v, 2) : EM_DASH;
  return shown_avg === EM_DASH ? null : shown_avg;
};

const flag_label = (code, avg) => {
  if (code === FLAG_LARGE) return 'Large amount';
  if (code === FLAG_ABOVE_AVERAGE)
    return avg ? `Above 3-month average (${avg})` : 'Above 3-month average';
  return str(code) || EM_DASH;
};

const flag_view = (code, text, avg) => ({
  code,
  label: flag_label(code, avg),
  text,
  tone: FLAG_TONES[code] || 'orange'
});

// Prefer the server's flags; an older server only sends large_amount.
const row_flags = (r, avg) => {
  if (!Array.isArray(r.flags))
    return r.large_amount === true ? [flag_view(FLAG_LARGE, '', avg)] : [];
  return r.flags
    .filter((f) => f && typeof f === 'object')
    .map((f) => flag_view(str(f.code), str(f.text), avg));
};

const has_flag = (code) => (r) => r.flags.some((f) => f.code === code);

export const eligibility_view = (data) => {
  const d = data || {};
  const eligible = arr(d.eligible).map((r) => {
    const avg = average(r.three_month_average);
    return {
      teacher_uuid: str(r.teacher_uuid),
      teacher_name: teacher_name(r.teacher_name),
      amount: money(r.amount),
      mode: MODES[r.mode] || shown(r.mode),
      bank_name: shown(r.bank_name),
      account_number_masked: shown(r.account_number_masked),
      large_amount: r.large_amount === true,
      retry: r.retry === true,
      three_month_average: avg,
      flags: row_flags(r, avg)
    };
  });
  return {
    pay_month: str(d.pay_month),
    cutover_month: str(d.cutover_month),
    next_round: typeof d.next_round === 'number' ? d.next_round : 1,
    active_files: arr(d.active_files).map(file_view),
    eligible,
    eligible_total: money(d.eligible_total),
    large_count: eligible.filter((r) => r.large_amount || has_flag(FLAG_LARGE)(r)).length,
    above_average_count: eligible.filter(has_flag(FLAG_ABOVE_AVERAGE)).length,
    retry_count: eligible.filter((r) => r.retry).length,
    left_out: arr(d.left_out).map(left_out_view)
  };
};

export const files_view = (data) => arr(data && data.files).map(file_view);

export const generate_view = (data) => {
  const d = data || {};
  return {
    file: file_view(d.file),
    left_out: arr(d.left_out).map(left_out_view),
    cancelled_lines: typeof d.cancelled_lines === 'number' ? d.cancelled_lines : 0,
    replay: d.replay === true
  };
};

/*
 * What Generate may do for the month. No file yet: R1. Otherwise a new round, plus a regenerate of
 * each active round the bank has not answered (an answered one is listed, disabled, with why).
 */
export const generate_choices = (eligibility, files) => {
  const all = arr(files);
  if (all.length === 0) {
    return [
      {
        key: 'R1',
        kind: KIND_R1,
        round_no: 1,
        label: 'Generate R1',
        file: null,
        file_uuid: '',
        disabled_text: ''
      }
    ];
  }
  const next =
    (eligibility && eligibility.next_round) || 1 + Math.max(...all.map((f) => f.round_no));
  const regen = all
    .filter((f) => f.status === 'ACTIVE')
    .map((f) => ({
      key: `REGEN-${f.uuid}`,
      kind: KIND_REGENERATE,
      round_no: f.round_no,
      label: `Regenerate R${f.round_no}`,
      file: f,
      file_uuid: f.uuid,
      disabled_text: f.answered
        ? 'The bank has already answered lines of this file, so it was uploaded: use a new round instead.'
        : ''
    }));
  return regen.concat([
    {
      key: 'NEW',
      kind: KIND_NEW_ROUND,
      round_no: next,
      label: `New round R${next}`,
      file: null,
      file_uuid: '',
      disabled_text: ''
    }
  ]);
};

// S6: Ops type the month itself, so a file is never made for the month next to the one they meant.
export const confirm_month_ok = (typed, pay_month) =>
  !!pay_month && str(typed).trim() === pay_month;

export const regenerate_warning = (file) => {
  const f = file || {};
  const n = (f.line_counts && f.line_counts.IN_FILE) || 0;
  return (
    `Make sure R${f.round_no} (${f.file_name || 'the earlier file'}) was NOT uploaded to the bank. ` +
    `Regenerating cancels its ${n} unpaid ${n === 1 ? 'line' : 'lines'} and makes a new file; if the bank ` +
    'also pays the earlier file, those teachers are paid twice.'
  );
};

export const downloads_text = (file) => {
  const list = arr(file && file.downloads);
  if (list.length === 0) return 'Never downloaded.';
  return `Downloaded ${list.length === 1 ? 'once' : `${list.length} times`}: ${list
    .map((x) => `${x.by} at ${x.at} IST`)
    .join('; ')}.`;
};

export const new_round_note = (round_no) =>
  `R${round_no} pays only teachers who are not already in a file waiting for the bank's answer. ` +
  'Upload it to the bank as a separate file.';

export const generate_done_text = (view) => {
  const v = view || {};
  const f = v.file || {};
  if (v.replay)
    return `This file was already made: ${f.file_name}. Download it from the files list.`;
  const cancelled = v.cancelled_lines
    ? ` ${v.cancelled_lines} ${v.cancelled_lines === 1 ? 'line' : 'lines'} of the earlier file cancelled.`
    : '';
  return `Bank file R${f.round_no} made: ${f.row_count} ${f.row_count === 1 ? 'teacher' : 'teachers'}, ${f.total}.${cancelled}`;
};

const SAFE_FILE_NAME_RE = /^[A-Za-z0-9._-]{1,200}$/;

// The server's own file name when it is a plain one, else a neutral fallback (never steers the save path).
export const bank_file_name = (file, header_name = '') => {
  if (SAFE_FILE_NAME_RE.test(header_name)) return header_name;
  const name = str(file && file.file_name);
  if (SAFE_FILE_NAME_RE.test(name)) return name;
  return `bank_file_R${(file && file.round_no) || 0}.csv`;
};

// ---- bank response ----

const line_view = (l) => {
  const d = l || {};
  return {
    line_id: typeof d.line_id === 'number' ? d.line_id : 0,
    line_no: typeof d.line_no === 'number' ? d.line_no : 0,
    status_code: shown(d.status_code),
    beneficiary_name: shown(d.beneficiary_name),
    account_last4: str(d.account_last4),
    ifsc: shown(d.ifsc),
    amount: money(d.amount),
    amount_raw: typeof d.amount === 'number' ? d.amount : null,
    value_date: str(d.value_date),
    bank_reference: str(d.bank_reference),
    bank_reason: str(d.bank_reason),
    match: match_view(d.match_result),
    proposed: proposed_view(d.proposed_action),
    proposed_action: str(d.proposed_action),
    applied: applied_view(d.applied_state),
    applied_state: str(d.applied_state),
    ops_action: d.ops_action ? ops_action_label(d.ops_action) : '',
    action_note: str(d.action_note),
    teacher_uuid: str(d.teacher_uuid),
    teacher_name: str(d.teacher_name),
    pay_month: str(d.pay_month),
    payout_item_uuid: str(d.payout_item_uuid),
    item_state: d.item_state ? item_state_view(d.item_state) : null,
    response_uuid: str(d.response_uuid)
  };
};

export const held_view = (data) => ({
  rows: arr(data && data.rows).map(line_view),
  next_after_id: cursor(data && data.next_after_id)
});

export const response_view = (data) => {
  const d = data || {};
  const sum = d.summary || {};
  const by_action = sum.by_action && typeof sum.by_action === 'object' ? sum.by_action : {};
  const count = (k) => by_action[k] || 0;
  const held = Object.keys(by_action)
    .filter((k) => k.indexOf('HOLD_') === 0)
    .reduce((n, k) => n + by_action[k], 0);
  return {
    response_uuid: str(d.response_uuid),
    pay_month: str(d.pay_month),
    file_name: shown(d.file_name),
    status: str(d.status),
    applied: str(d.status) === 'APPLIED',
    uploaded_by: shown(d.uploaded_by_name),
    uploaded_at: shown(d.uploaded_at_ist),
    applied_at: str(d.applied_at_ist),
    already_uploaded: d.already_uploaded === true,
    already_applied: d.already_applied === true,
    lines: arr(d.lines).map(line_view),
    summary: {
      lines: sum.lines || 0,
      sent: count('MARK_SENT'),
      failed: count('MARK_FAILED'),
      no_change: count('NO_CHANGE'),
      ignored: count('IGNORE'),
      held,
      sent_total: money(sum.sent_total),
      failed_total: money(sum.failed_total),
      double_payment_alerts: sum.double_payment_alerts || 0
    }
  };
};

export const apply_confirm_text = (view) => {
  const s = (view && view.summary) || {};
  return (
    `Apply this bank response? ${s.sent} marked sent (${s.sent_total}), ${s.failed} marked failed ` +
    `(${s.failed_total}), ${s.held} held for Ops, ${s.ignored} ignored, ${s.no_change} unchanged. ` +
    'Teachers are told about sent and failed payments.'
  );
};

export const response_file_error = (file) => {
  if (!file) return 'Choose the bank response file.';
  if (file.size > RESPONSE_MAX_BYTES) return 'The file is larger than 2 MB: is it the right file?';
  if (file.size === 0) return 'The file is empty.';
  return '';
};

// ---- teacher-month drill-down ----

const item_view = (it) => {
  const d = it || {};
  return {
    uuid: str(d.uuid),
    state: str(d.state),
    state_view: item_state_view(d.state),
    amount: money(d.amount),
    round_no: typeof d.round_no === 'number' ? `R${d.round_no}` : 'Outside file',
    reference: shown(d.reference),
    close_version_no: d.close_version_no,
    sent_on: shown(d.sent_on),
    bank_reference: shown(d.bank_reference),
    why: str(d.bank_reason) || str(d.held_reason),
    account: [str(d.bank_name), str(d.account_number_masked)].filter(Boolean).join(' ') || EM_DASH,
    retry_allowed: d.retry_allowed === true,
    resolution_note: str(d.resolution_note),
    created_at: shown(d.created_at_ist)
  };
};

const claim_view = (c) => {
  const d = c || {};
  return {
    claim_uuid: str(d.claim_uuid),
    status: str(d.status),
    status_view: claim_status_view(d.status),
    teacher_uuid: str(d.teacher_uuid),
    teacher_name: teacher_name(d.teacher_name),
    pay_month: shown(d.pay_month),
    pay_month_raw: str(d.pay_month),
    payout_item_uuid: str(d.payout_item_uuid),
    amount: money(d.amount),
    sent_on: shown(d.sent_on),
    bank_reference: shown(d.bank_reference),
    note: str(d.note),
    created_at: shown(d.created_at_ist),
    resolved_by: str(d.resolved_by),
    resolved_at: str(d.resolved_at_ist),
    resolution_note: str(d.resolution_note)
  };
};

export const teacher_month_view = (data) => {
  const d = data || {};
  const tv = d.teacher_view || {};
  const elig = d.eligibility || {};
  const outstanding = typeof d.outstanding === 'number' ? d.outstanding : null;
  return {
    teacher_uuid: str(d.teacher_uuid),
    teacher_name: teacher_name(d.teacher_name),
    pay_month: str(d.pay_month),
    lifecycle: str(d.lifecycle),
    payable: money(d.payable),
    paid_so_far: money(d.paid_so_far),
    outstanding: money(outstanding),
    outstanding_raw: outstanding,
    eligibility_text: str(elig.reason)
      ? str(elig.reason_text) || str(elig.reason)
      : 'The next bank file pays this month.',
    teacher_headline: shown(tv.headline),
    teacher_state: teacher_state_view(tv.state),
    items: arr(d.items).map(item_view),
    claims: arr(d.claims).map(claim_view),
    // M4: only a closed month with money still owed can be settled outside the file.
    can_pay_outside: str(d.lifecycle) === 'CLOSED' && outstanding !== null && outstanding > 0,
    // Only an explicit false: an older server omits the flag, and then the 409 path decides.
    outside_full_only: d.has_confirmed_invoice === false
  };
};

export const claims_view = (data) => ({
  rows: arr(data && data.rows).map(claim_view),
  next_after_id: cursor(data && data.next_after_id)
});

const deleted_label = (r) => {
  const id = typeof r.teacher_id === 'number' ? `Teacher #${r.teacher_id}` : str(r.teacher_uuid);
  const name = str(r.teacher_name);
  return name && id ? `${name} (${id})` : name || id;
};

export const deleted_view = (data) => ({
  rows: arr(data && data.rows).map((r) => ({
    teacher_uuid: str(r.teacher_uuid),
    // The name is the server's placeholder for a deleted account; the id is what Ops search by.
    teacher_label: deleted_label(r),
    total: money(r.total),
    account:
      [str(r.approved_bank_name), str(r.account_number_masked)].filter(Boolean).join(' ') ||
      'No approved details',
    months: arr(r.months).map((m) => ({
      pay_month: str(m && m.pay_month),
      lifecycle: str(m && m.lifecycle),
      outstanding: money(m && m.outstanding)
    }))
  })),
  next_after_teacher_id: cursor(data && data.next_after_teacher_id)
});

export const holidays_view = (data) =>
  arr(data && data.holidays)
    .map((h) => ({ date: str(h && h.date), name: shown(h && h.name) }))
    .sort((a, b) => (a.date < b.date ? -1 : 1));

// This IST year and the next: holidays are entered ahead, never years back.
export const holiday_years = (today) => {
  const y = Number(str(today).slice(0, 4)) || new Date().getUTCFullYear();
  return [y, y + 1];
};

// ---- validation ----

const AMOUNT_RE = /^\d{1,10}(\.\d{1,2})?$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const REFERENCE_RE = /^[A-Za-z0-9/_.-]{1,64}$/;

// Rupee text to whole paise without floating point; null when it is not a bank amount.
export const amount_paise = (raw) => {
  const v = str(raw).trim();
  if (!AMOUNT_RE.test(v)) return null;
  const [whole, frac = ''] = v.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
};

// A wire amount (rupees with paise) as whole paise.
export const rupees_paise = (n) => (typeof n === 'number' ? Math.round(n * 100) : null);

const real_date = (v) => {
  if (!DATE_RE.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};

export const past_date_error = (raw, today) => {
  const v = str(raw).trim();
  if (!v) return 'Enter the date.';
  if (!real_date(v) || v > today) return 'Enter the date as YYYY-MM-DD, not in the future.';
  return '';
};

export const reference_error = (raw, required = true) => {
  const v = str(raw).trim();
  if (!v) return required ? 'Enter the payment reference.' : '';
  return REFERENCE_RE.test(v) ? '' : 'Use letters, digits and / _ . - only, up to 64 characters.';
};

export const payout_reason_error = (raw, what = 'reason') => {
  const n = reason_length(raw);
  if (n === 0) return `Write the ${what}.`;
  if (n < REASON_MIN_LEN) return `The ${what} must be at least ${REASON_MIN_LEN} characters.`;
  if (n > REASON_MAX_LEN) return `The ${what} must be at most ${REASON_MAX_LEN} characters.`;
  return '';
};

// Whole-paise wire rupees as the form's amount text ("2500.00"), so a locked full amount matches exactly.
export const full_amount_text = (outstanding) => {
  const p = rupees_paise(outstanding);
  if (p === null || p < 0) return '';
  return `${Math.floor(p / 100)}.${String(p % 100).padStart(2, '0')}`;
};

// form: {amount, paid_on, reference, reason}; outstanding: the month's wire value (rupees).
// full_only: the server said no confirmed invoice, so only the whole outstanding may be recorded.
export const paid_outside_errors = (form, outstanding, today, full_only = false) => {
  const f = form || {};
  const paise = amount_paise(f.amount);
  const cap = rupees_paise(outstanding);
  let amount = '';
  if (!str(f.amount).trim()) amount = 'Enter the amount.';
  else if (paise === null || paise <= 0)
    amount = 'Enter the amount in rupees with at most 2 decimals, above zero.';
  else if (cap !== null && paise > cap)
    amount = `The amount is more than this month still owes (${fmt(outstanding, 2)}).`;
  else if (full_only && cap !== null && paise !== cap) amount = PARTIAL_NEEDS_INVOICE_TEXT;
  return {
    amount,
    paid_on: past_date_error(f.paid_on, today),
    reference: reference_error(f.reference),
    reason: payout_reason_error(f.reason)
  };
};

// The stored copy of a bad UTR may be cut, so the server refuses it: Ops must type the reference.
export const bad_utr = (line) => !!line && line.proposed_action === 'HOLD_BAD_UTR';

// line: a held line_view; form: {action, reason, bank_reference, sent_on, payout_item_uuid}.
export const resolve_errors = (line, form, today) => {
  const l = line || {};
  const f = form || {};
  const out = {
    action: '',
    reason: payout_reason_error(f.reason),
    bank_reference: '',
    sent_on: '',
    payout_item_uuid: ''
  };
  if (!OPS_ACTIONS.some((a) => a.value === f.action))
    out.action = 'Choose what to do with this line.';
  if (f.action === ACTION_SENT) {
    out.bank_reference = reference_error(f.bank_reference, !l.bank_reference || bad_utr(l));
    out.sent_on = str(f.sent_on).trim() || !l.value_date ? past_date_error(f.sent_on, today) : '';
  }
  if ((f.action === ACTION_SENT || f.action === ACTION_FAILED) && !l.payout_item_uuid) {
    out.payout_item_uuid = str(f.payout_item_uuid).trim()
      ? ''
      : 'This line matched no payment: enter the payment ID it answers (from the teacher-month view).';
  }
  return out;
};

export const holiday_errors = (date, name) => ({
  date:
    DATE_RE.test(str(date).trim()) && real_date(str(date).trim())
      ? ''
      : 'Enter the date as YYYY-MM-DD.',
  name: (() => {
    const n = Array.from(str(name).trim()).length;
    if (n === 0) return 'Enter the holiday name.';
    return n > HOLIDAY_NAME_MAX ? `The name must be at most ${HOLIDAY_NAME_MAX} characters.` : '';
  })()
});

export const has_error = (errors) => Object.values(errors || {}).some(Boolean);

// ---- errors ----

const STALE_CODES = [
  'PAYOUT_LINE_NOT_HELD',
  'PAYOUT_CLAIM_ALREADY_RESOLVED',
  'PAYOUT_FILE_NOT_ACTIVE',
  'PAYOUT_FILE_ALREADY_ANSWERED',
  'PAYOUT_ELIGIBILITY_CHANGED',
  'PAYOUT_ITEM_STATE_CONFLICT',
  'PAYOUT_RETRY_NOT_LATEST',
  'PAYOUT_CHOOSE_REGENERATE_OR_NEW_ROUND',
  'PAYOUT_NO_FILE_YET'
];

/*
 * One reader for every payout failure. kind: 'auth' (401: the shell signs in again) | 'forbidden' (403:
 * this tab only) | 'dropped' (no answer: the same request may be retried with the same id) | 'preview_changed'
 * | 'before_cutover' | 'full_only' (paid outside must be the whole outstanding) | 'stale' (the screen is out
 * of date: reload it) | 'inline'.
 */
export const payout_error = (e, fallback) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  const text = message || fallback;
  if (!e || !e.response) {
    return {
      kind: 'dropped',
      code: '',
      text: 'No answer from the server. It may have been saved: try again.'
    };
  }
  if (status === 401) return { kind: 'auth', code, text };
  if (status === 403 || code === 'FORBIDDEN')
    return { kind: 'forbidden', code, text: message || FORBIDDEN_TEXT };
  if (code === 'PAYOUT_PREVIEW_CHANGED') {
    // The 409 carries the refreshed ResponseView in data.
    const fresh = e.response.data && e.response.data.data;
    return { kind: 'preview_changed', code, text, fresh: fresh || null };
  }
  if (code === 'PAYOUT_PARTIAL_NEEDS_INVOICE')
    return { kind: 'full_only', code, text: PARTIAL_NEEDS_INVOICE_TEXT };
  if (code === 'PAYOUT_BEFORE_CUTOVER')
    return { kind: 'before_cutover', code, text: message || BEFORE_CUTOVER_TEXT };
  if (STALE_CODES.indexOf(code) !== -1) return { kind: 'stale', code, text };
  return { kind: 'inline', code, text };
};

// A dropped request keeps its client_request_id so a retry replays it; any real answer retires it.
export const keeps_request_id = (err) => err.kind === 'dropped';

export const resolve_done_text = (data, action) => {
  const over = data && typeof data.overpaid === 'number' && data.overpaid > 0 ? data.overpaid : 0;
  const base = `Line resolved: ${ops_action_label(action).toLowerCase()}.`;
  return over
    ? `${base} This teacher-month is now paid ${fmt(over, 2)} more than it owes: recover it.`
    : base;
};
